/**
 * Generic DocType List Methods
 * 
 * Provides doctype-agnostic list operations that can be reused across all doctypes.
 * Item-specific logic should remain in items.ts and delegate to these generic functions.
 */

import { frappeListWithPagination, frappeMethod } from "./utils";
import { getERPClient } from "./client";
import { tenantServer } from "@/lib/api/tenantServerClient";
import { useAuthStore } from "@/lib/store/authStore";
import type { FrappeFilterArray } from "./filterMapper";
import type { FrappeListResponse } from "@/lib/frappe-client";

/**
 * Value types allowed in Frappe filters
 * Covers 99% of use cases while keeping types honest
 */
export type FrappeFilterValue = 
  | string 
  | number 
  | boolean 
  | null 
  | undefined 
  | (string | number | boolean)[];

/**
 * Frappe filter format for list views
 * 
 * 3-tuple: [fieldname, operator, value] (for target doctype fields)
 * 4-tuple: [child_doctype, fieldname, operator, value] (for child table fields)
 * 
 * Examples:
 *   ["item_code", "=", "ITEM-001"]  // 3-tuple
 *   ["Sales Order Item", "item_code", "=", "ITEM-001"]  // 4-tuple
 */
export type FrappeFilter3 = [string, string, any];
export type FrappeFilter4 = [string, string, string, any];
export type FrappeFilter = FrappeFilter3 | FrappeFilter4;

type DocTypeListBackendParams = {
  doctype: string;
  filters?: FrappeFilterArray;
  or_filters?: Array<[string, string, FrappeFilterValue]>;
  pageIndex?: number;
  pageSize?: number;
  orderBy?: string;
  fields?: string[];
};

/**
 * Generic list with standard Resource API
 * 
 * Uses /api/resource/<doctype> endpoint with standard Frappe filtering.
 */
export async function docTypeListWithPagination<T>(
  params: DocTypeListBackendParams,
): Promise<FrappeListResponse<T>> {
  const {
    doctype,
    filters,
    or_filters,
    pageIndex = 0,
    pageSize = 20,
    orderBy,
    fields,
  } = params;

  return frappeListWithPagination<T>(doctype, {
    filters,
    or_filters,
    limit_start: pageIndex * pageSize,
    limit_page_length: pageSize,
    order_by: orderBy || "modified desc",
    fields,
  });
}

type DocTypeListViaMethodParams = DocTypeListBackendParams & {
  method: string; // e.g. "zaviago_backend.api.item_list.list_items_with_assignment"
};

/**
 * Generic list via method override (for special doctypes)
 * 
 * Uses a custom whitelisted method that bypasses Frappe's standard filter validation.
 * Useful for doctypes that need special filtering logic (e.g., _assigned_to via ToDo JOIN).
 */
export async function docTypeListWithPaginationViaMethod<T>(
  params: DocTypeListViaMethodParams,
): Promise<FrappeListResponse<T>> {
  const {
    doctype,
    filters,
    or_filters,
    pageIndex = 0,
    pageSize = 20,
    orderBy,
    method,
    fields,
  } = params;

  // Call the method - frappeMethod already extracts data.message, so result should be the unwrapped object
  const result = await frappeMethod<unknown>(method, {
    doctype,
    filters: filters ? JSON.stringify(filters) : undefined,
    or_filters: or_filters ? JSON.stringify(or_filters) : undefined,
    fields: fields ? JSON.stringify(fields) : undefined,
    limit_start: pageIndex * pageSize,
    limit_page_length: pageSize,
    order_by: orderBy || "modified desc",
    as_list: false,
  });

  /**
   * IMPORTANT:
   * - MethodClient.call() already returns `data.message || data`
   * - Do NOT "double unwrap" `.message` here; it can drop valid payloads that legitimately contain a `message` key.
   *
   * Be defensive about shape (custom list methods sometimes return arrays),
   * but do not silently coerce unknown shapes to empty lists.
   */
  if (Array.isArray(result)) {
    return {
      data: result as T[],
      total: result.length,
      pageLength: pageSize,
    };
  }

  if (!result || typeof result !== "object") {
    throw new Error(
      `[docTypeListWithPaginationViaMethod] Invalid response shape from "${method}": expected object/array`
    );
  }

  const obj = result as {
    data?: unknown;
    total_count?: unknown;
    total?: unknown;
    page_length?: unknown;
    pageLength?: unknown;
  };

  if (!Array.isArray(obj.data)) {
    throw new Error(
      `[docTypeListWithPaginationViaMethod] Invalid response shape from "${method}": expected { data: any[] }`
    );
  }

  return {
    data: obj.data as T[],
    total:
      typeof obj.total_count === "number"
        ? obj.total_count
        : typeof obj.total === "number"
          ? obj.total
          : obj.data.length,
    pageLength:
      typeof obj.page_length === "number"
        ? obj.page_length
        : typeof obj.pageLength === "number"
          ? obj.pageLength
          : pageSize,
  };
}

/**
 * Toggle like/favorite status for a document
 * 
 * Uses Frappe's standard like endpoint: frappe.desk.like.toggle_like
 * 
 * Note: Frappe expects add to be "Yes" (to add like) or "No" (to remove like),
 * or a boolean that gets converted. We use "Yes"/"No" strings for clarity.
 */
export async function toggleLike(params: {
  doctype: string;
  name: string;
  add: boolean;
}): Promise<void> {
  const { doctype, name, add } = params;
  
  // Use our safe wrapper instead of Frappe core toggle_like.
  // Frappe's core implementation inserts a "Like" Comment, which can trigger hooks
  // and crash in some app/version combinations. We only need `_liked_by` persistence.
  await frappeMethod("zaviago_backend.api.likes.toggle_like_safe", {
    doctype,
    name,
    add: add ? "Yes" : "No",
  });
}

/**
 * Enriched assignment data from ToDo records
 */
export type DocSidebarAssignment = {
  user: string;
  full_name: string;
  avatar?: string | null;
  type: "User";  // Always "User" for now (groups expand to users)
  user_group?: string | null;  // Optional, always null for now
  priority: "Low" | "Medium" | "High" | "Urgent";
  complete_by?: string | null;  // ISO date string
  comment?: string | null;
  assigned_by?: string | null;
  assigned_on?: string | null;  // ISO datetime string
};

export type DocSidebarShare = {
  user: string;
  full_name: string;
  avatar?: string | null;
  can_read: boolean;
  can_write: boolean;
  can_share: boolean;
  can_submit: boolean;
  shared_by?: string | null;
  shared_on?: string | null;  // ISO datetime string
};

export type DocSidebarShareCapabilities = {
  can_read: boolean;
  can_write: boolean;
  can_share: boolean;
  can_submit: boolean;
};

export type DocSidebarPublicShare = {
  enabled: boolean;
  can_read: boolean;
  can_write: boolean;
  can_share: boolean;
  can_submit: boolean;
  shared_by?: string | null;
  shared_on?: string | null;  // ISO datetime string
};

/**
 * Chat/Timeline types for document chat panel
 */
export type DocChatItemType = "comment" | "activity"; // comment = user chat, activity = system events

export type DocChatItem = {
  type: DocChatItemType;
  name: string;
  owner: string;
  user_full_name: string;
  user_avatar?: string | null;
  content: string;      // HTML content or formatted activity text
  plain_text?: string;  // optional
  creation: string;     // ISO datetime string
  mentions?: string[];  // user ids/emails
  mentions_detail?: {
    user: string;
    full_name: string;
    avatar?: string | null;
  }[];
  meta?: {
    // optional metadata for activity (status from/to, field name, etc.)
    event_type?: string; // e.g. "Info", "Edit", "Assignment", "Status Change"
  };
  reply_to?: string | null;
  reply_to_detail?: {
    name: string;
    owner: string;
    user_full_name: string;
    content: string;
    is_deleted: boolean;
  } | null;
  reactions?: {
    emoji: string;
    count: number;
    users: string[];
    has_reacted: boolean;
  }[];
};

export type DocChatResponse = {
  items: DocChatItem[];
  has_more: boolean;
  next_before?: string | null;
};

/**
 * Sidebar data type for document sidebar
 */
export type DocSidebarData = {
  image: string | null;
  primaryVideo?: {
    url: string;
    alt_text?: string;
  } | null;
  assigned_to: DocSidebarAssignment[];
  attachments: { name: string; file_name: string; file_url: string; creation: string }[];
  tags: string[];
  liked_by: string[];
  like_count: number;
  comment_count: number;
  is_followed: boolean;
  history: {
    name: string;
    comment_type: string;
    content: string;
    owner: string;
    creation: string;
  }[];
  shares: DocSidebarShare[];
  share_capabilities: DocSidebarShareCapabilities;
  public_share: DocSidebarPublicShare;
};

/**
 * Get sidebar data for a document
 * 
 * Returns image, assigned users, attachments, tags, likes, comments, follow status, and history.
 */
export async function getSidebar(doctype: string, name: string): Promise<DocSidebarData> {
  // Validate inputs to prevent API errors
  if (!doctype || !name) {
    throw new Error(`getSidebar requires both doctype and name. Received: doctype="${doctype}", name="${name}"`);
  }
  
  return frappeMethod<DocSidebarData>("zaviago_backend.api.doc_sidebar.get_doc_sidebar", {
    doctype,
    name,
  });
}

/**
 * Toggle follow status for a document
 * 
 * Uses our backend wrapper which ensures consistency with get_doc_sidebar.
 */
export async function toggleFollow(
  doctype: string,
  name: string,
  following: boolean
): Promise<{ status: string; following: boolean }> {
  return frappeMethod<{ status: string; following: boolean }>(
    "zaviago_backend.api.doc_sidebar.toggle_follow",
    {
      doctype,
      name,
      following,
    }
  );
}

/**
 * Get tag suggestions for autocomplete
 */
export async function getTagSuggestions(
  doctype: string,
  txt: string
): Promise<string[]> {
  return frappeMethod<string[]>("zaviago_backend.api.doc_sidebar.get_tag_suggestions", {
    doctype,
    txt,
  });
}

/**
 * Upload a file attachment to a document
 *
 * Mode-aware: uses JWT auth in control_plane mode, CSRF+session in direct mode
 */
export async function uploadAttachment(params: {
  doctype: string;
  name: string;
  file: File;
}): Promise<{ file_url: string; file_name: string; name: string }> {
  const { doctype, name, file } = params;
  const authMode = useAuthStore.getState().authMode;

  // Control Plane mode - use JWT-authenticated upload
  if (authMode === 'control_plane') {
    return tenantServer.uploadFile(file, {
      doctype,
      docname: name,
      isPrivate: false,
    });
  }

  // Direct mode - use session-based upload with CSRF
  const frappeUrl = useAuthStore.getState().frappeUrl;
  if (!frappeUrl) {
    throw new Error("Not authenticated. Please log in.");
  }

  // Get CSRF token
  const csrfToken = await getCsrfToken(frappeUrl);

  // Create FormData
  const formData = new FormData();
  formData.append("file", file);
  formData.append("doctype", doctype);
  formData.append("docname", name);
  formData.append("is_private", "0"); // Public by default

  // Construct upload URL
  const baseUrl = frappeUrl.replace(/\/$/, "");
  // If baseUrl is "/api" (Vite proxy), don't add another "/api" prefix
  // If baseUrl is absolute (http://...), add "/api" prefix
  const apiPrefix = baseUrl === "/api" ? "" : "/api";
  const uploadUrl = `${baseUrl}${apiPrefix}/method/upload_file`;

  // Upload using fetch (since it's FormData, not JSON)
  const response = await fetch(uploadUrl, {
    method: "POST",
    headers: {
      "X-Frappe-CSRF-Token": csrfToken,
    },
    credentials: "include", // Include cookies for session auth
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Upload failed: ${response.statusText} - ${errorText}`);
  }

  const result = await response.json();
  const fileDoc = result.message;
  
  return {
    name: fileDoc.name,
    file_url: fileDoc.file_url,
    file_name: fileDoc.file_name,
  };
}

/**
 * Helper to get CSRF token from cookies or API
 */
async function getCsrfToken(baseUrl: string): Promise<string> {
  // Try to get from cookies first (if in browser)
  if (typeof document !== "undefined") {
    const cookies = document.cookie.split(";");
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split("=");
      // Frappe uses various cookie names for CSRF token
      if (name === "csrf_token" || name === "csrf-token" || name === "csrf_token_http") {
        return decodeURIComponent(value);
      }
    }
  }
  
  // Fallback to API call - Use our whitelisted endpoint
  try {
    // If baseUrl is "/api" (Vite proxy), don't add another "/api" prefix
    // If baseUrl is absolute (http://...), add "/api" prefix
    const apiPrefix = baseUrl === "/api" ? "" : "/api";
    const response = await fetch(`${baseUrl}${apiPrefix}/method/zaviago_backend.api.session.get_csrf_token`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Accept": "application/json",
      },
    });
    if (response.ok) {
      const result = await response.json();
      // Our endpoint returns { "message": { "csrf_token": "..." } } (Frappe wrapper)
      // or directly { "csrf_token": "..." } depending on Frappe version
      const token = result.message?.csrf_token ?? result.csrf_token ?? null;
      if (token) {
        return token;
      }
    }
  } catch (error) {
    // If that fails, log and return empty (server might still accept with session cookies)
    if (import.meta.env.DEV) {
      console.warn("[docs.ts] Failed to fetch CSRF token:", error);
    }
  }
  return "";
}

/**
 * Delete an attachment (File document)
 *
 * Mode-aware: uses JWT auth in control_plane mode, session in direct mode
 */
export async function deleteAttachment(fileName: string): Promise<void> {
  const authMode = useAuthStore.getState().authMode;

  if (authMode === 'control_plane') {
    await tenantServer.delete("File", fileName);
    return;
  }

  const client = getERPClient();
  await client.delete({ doctype: "File", name: fileName });
}

/**
 * Add a tag to a document
 * 
 * Uses Frappe's tag API: frappe.desk.doctype.tag.tag.add_tag
 */
export async function addTag(doctype: string, name: string, tag: string): Promise<void> {
  await frappeMethod("frappe.desk.doctype.tag.tag.add_tag", {
    tag,
    dt: doctype,
    dn: name,
  });
}

/**
 * Remove a tag from a document
 * 
 * Uses Frappe's tag API: frappe.desk.doctype.tag.tag.remove_tag
 */
export async function removeTag(doctype: string, name: string, tag: string): Promise<void> {
  await frappeMethod("frappe.desk.doctype.tag.tag.remove_tag", {
    tag,
    dt: doctype,
    dn: name,
  });
}

/**
 * Assign a document to users and/or user groups with optional metadata
 * 
 * Uses our backend wrapper which enriches assignments with priority, complete_by, comment
 */
export async function assignDoc(params: {
  doctype: string;
  name: string;
  users?: string[];
  user_groups?: string[];
  priority?: "Low" | "Medium" | "High" | "Urgent";
  complete_by?: string;  // ISO date string
  comment?: string;
}): Promise<{ status: string; assigned_to: DocSidebarAssignment[] }> {
  return frappeMethod<{ status: string; assigned_to: DocSidebarAssignment[] }>(
    "zaviago_backend.api.doc_sidebar.assign_doc",
    params
  );
}

/**
 * Unassign a user or user group from a document
 * 
 * Uses our backend wrapper which uses Frappe's assign_to.remove API
 */
export async function unassignDoc(params: {
  doctype: string;
  name: string;
  user?: string;
  user_group?: string;
}): Promise<{ status: string; assigned_to: DocSidebarAssignment[] }> {
  return frappeMethod<{ status: string; assigned_to: DocSidebarAssignment[] }>(
    "zaviago_backend.api.doc_sidebar.unassign_doc",
    params
  );
}

/**
 * Assign current user to a document (convenience method)
 */
export async function assignToMe(params: {
  doctype: string;
  name: string;
  priority?: "Low" | "Medium" | "High" | "Urgent";
  complete_by?: string;  // ISO date string
  comment?: string;
}): Promise<{ status: string; assigned_to: DocSidebarAssignment[] }> {
  return frappeMethod<{ status: string; assigned_to: DocSidebarAssignment[] }>(
    "zaviago_backend.api.doc_sidebar.assign_to_me",
    params
  );
}

/**
 * Search users for assignment suggestions
 */
export async function searchAssignUsers(
  txt: string = "",
  limit: number = 10
): Promise<{ value: string; label: string; avatar?: string | null }[]> {
  return frappeMethod<{ value: string; label: string; avatar?: string | null }[]>(
    "zaviago_backend.api.doc_sidebar.search_assign_users",
    { txt, limit }
  );
}

/**
 * Search user groups for assignment suggestions
 */
export async function searchAssignUserGroups(
  txt: string = "",
  limit: number = 10
): Promise<{ value: string; label: string }[]> {
  return frappeMethod<{ value: string; label: string }[]>(
    "zaviago_backend.api.doc_sidebar.search_assign_user_groups",
    { txt, limit }
  );
}

/**
 * Set cover image for a document
 */
export async function setCoverImage(
  doctype: string,
  name: string,
  fileUrl: string
): Promise<{ status: string; image: string }> {
  return frappeMethod<{ status: string; image: string }>(
    "zaviago_backend.api.doc_sidebar.set_cover_image",
    {
      doctype,
      name,
      file_url: fileUrl,
    }
  );
}

/**
 * Update or create a DocShare entry for a user on a document
 */
export async function updateDocShare(params: {
  doctype: string;
  name: string;
  user: string;
  can_read?: boolean;
  can_write?: boolean;
  can_share?: boolean;
  can_submit?: boolean;
}): Promise<{
  status: string;
  shares: DocSidebarShare[];
  share_capabilities: DocSidebarShareCapabilities;
}> {
  return frappeMethod<{
    status: string;
    shares: DocSidebarShare[];
    share_capabilities: DocSidebarShareCapabilities;
  }>("zaviago_backend.api.doc_sidebar.update_doc_share", params);
}

/**
 * Delete a DocShare entry for a user on a document
 */
export async function deleteDocShare(params: {
  doctype: string;
  name: string;
  user: string;
}): Promise<{ status: string; shares: DocSidebarShare[] }> {
  return frappeMethod<{ status: string; shares: DocSidebarShare[] }>(
    "zaviago_backend.api.doc_sidebar.delete_doc_share",
    params
  );
}

/**
 * Update or create the "Everyone" (public) share for a document
 */
export async function updatePublicShare(params: {
  doctype: string;
  name: string;
  can_read: boolean;
  can_write?: boolean;
  can_share?: boolean;
  can_submit?: boolean;
}): Promise<{
  status: string;
  public_share: DocSidebarPublicShare;
  share_capabilities: DocSidebarShareCapabilities;
}> {
  return frappeMethod<{
    status: string;
    public_share: DocSidebarPublicShare;
    share_capabilities: DocSidebarShareCapabilities;
  }>("zaviago_backend.api.doc_sidebar.update_public_share", params);
}

/**
 * Get paginated chat timeline for a document
 * 
 * Returns comments in chronological order (oldest first).
 * Supports cursor-based pagination using 'before' parameter.
 */
export async function getDocChat(
  doctype: string,
  name: string,
  params?: { limit?: number; before?: string }
): Promise<DocChatResponse> {
  return frappeMethod<DocChatResponse>(
    "zaviago_backend.api.doc_chat.get_doc_chat",
    {
      doctype,
      name,
      limit: params?.limit,
      before: params?.before,
    }
  );
}

/**
 * Add a new comment to a document
 */
export async function addDocComment(params: {
  doctype: string;
  name: string;
  content: string;
  mentions?: string[];
  reply_to?: string;
}): Promise<{ status: string; item: DocChatItem }> {
  return frappeMethod<{ status: string; item: DocChatItem }>(
    "zaviago_backend.api.doc_chat.add_doc_comment",
    params
  );
}

/**
 * Delete a comment (own messages only, within 15-minute window)
 */
export async function deleteDocComment(params: {
  comment_name: string;
}): Promise<{ status: string }> {
  return frappeMethod<{ status: string }>(
    "zaviago_backend.api.doc_chat_actions.delete_doc_comment",
    params
  );
}

/**
 * Toggle a reaction on a comment (add or remove)
 */
export async function toggleCommentReaction(params: {
  comment_name: string;
  emoji: string;
}): Promise<{
  action: "added" | "removed";
  reactions: NonNullable<DocChatItem["reactions"]>;
}> {
  return frappeMethod<{
    action: "added" | "removed";
    reactions: NonNullable<DocChatItem["reactions"]>;
  }>(
    "zaviago_backend.api.doc_chat_actions.toggle_comment_reaction",
    params
  );
}

/**
 * Document connections response types
 */
export interface LinkedDoc {
  name: string;
  status?: string;
  posting_date?: string;
  transaction_date?: string;
  customer?: string;
  supplier?: string;
  grand_total?: number;
  outstanding_amount?: number;
  link_field?: string;
  [key: string]: any;
}

export interface DocConnectionGroup {
  target_doctype: string;
  target_label: string;
  count: number;
  docs: LinkedDoc[];
  // Optional group metadata (can be set by backend or frontend config)
  group_id?: string;      // e.g. "pricing", "sales", "stock"
  group_label?: string;   // e.g. "Pricing", "Sales", "Stock"
}

export interface DocConnectionsResponse {
  doctype: string;
  name: string;
  groups: DocConnectionGroup[];
}

/**
 * Get document connections (linked documents)
 * 
 * Returns all documents linked to the source document, grouped by target doctype.
 */
export async function getDocConnections(
  doctype: string,
  name: string
): Promise<DocConnectionsResponse> {
  return frappeMethod<DocConnectionsResponse>(
    "zaviago_backend.api.links.get_doc_connections",
    { doctype, name }
  );
}

/**
 * Relationship Graph response types
 */
export interface RelationshipGraphGroup extends DocConnectionGroup {
  edge_id: string;
  icon?: string | null;
  has_more?: boolean;  // Indicates if more docs exist without leaking total count
  filters?: FrappeFilter[];  // Backend-provided filters for navigation
  resolution?: "query" | "structural_only" | "error";  // Resolution status from backend
  _debug?: {
    resolved_filters?: any[];
    query_time_ms?: number;
    docs_before_permission_filter?: number;
    docs_after_permission_filter?: number;
    preview_count?: number;
    has_more?: boolean;
    error?: string;
  };
}

export interface RelationshipGraphResponse {
  doctype: string;
  name?: string;
  enabled?: boolean;  // true if graph is enabled, false if allowlist-filtered
  reason?: string;    // "allowlist_filtered" if disabled due to allowlist
  groups: RelationshipGraphGroup[];
}

/**
 * Get relationship graph with materialized connection groups
 * 
 * Returns groups with counts and preview documents (matching get_doc_connections format).
 */
export async function getRelationshipGraph(
  doctype: string,
  docname?: string,
  debug?: boolean
): Promise<RelationshipGraphResponse> {
  return frappeMethod<RelationshipGraphResponse>(
    "zaviago_backend.api.relationship_graph.get_relationship_graph",
    { 
      doctype, 
      docname,
      debug: debug ? 1 : 0
    }
  );
}

/**
 * Document dashboard response types
 */
export interface DashboardTableColumn {
  id: string;
  label: string;
}

export interface DashboardChartSeries {
  id: string;
  label: string;
  data: { x: string; y: number }[];
}

export interface DashboardSection {
  id: string;
  label: string;
  type: "table" | "chart";
  meta?: {
    primary_key?: string;
    chart_type?: "bar" | "line" | "area" | "stock_balance";
    x_axis_field?: string;
    warehouse_count?: number;  // For stock_balance charts
    total_stock?: number;       // For stock_balance charts
  };
  columns?: DashboardTableColumn[];
  rows?: Record<string, any>[];
  series?: DashboardChartSeries[];
  error?: string;
}

export interface DocDashboardResponse {
  doctype: string;
  name: string;
  sections: DashboardSection[];
}

/**
 * Get document dashboard data
 * 
 * Returns dashboard sections (tables, charts) for a document.
 * Generic and reusable across doctypes.
 */
export async function getDocDashboard(
  doctype: string,
  name: string
): Promise<DocDashboardResponse> {
  return frappeMethod<DocDashboardResponse>(
    "zaviago_backend.api.dashboard.get_doc_dashboard",
    { doctype, name }
  );
}

/**
 * Generic docs operations
 */
export const docs = {
  docTypeListWithPagination,
  docTypeListWithPaginationViaMethod,
  toggleLike,
  getSidebar,
  toggleFollow,
  uploadAttachment,
  deleteAttachment,
  addTag,
  removeTag,
  getTagSuggestions,
  assignDoc,
  unassignDoc,
  assignToMe,
  searchAssignUsers,
  searchAssignUserGroups,
  setCoverImage,
  updateDocShare,
  deleteDocShare,
  updatePublicShare,
  getDocChat,
  addDocComment,
  getDocConnections,
  getRelationshipGraph,
  deleteDocComment,
  toggleCommentReaction,
};

